import React, { useState, useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import "./FiltroBecas.css";
import scholarships from "../../assets/data/becas_actualizadas.json";

const categoryColors = {
  "General / Interdisciplinaria": "#7c3cff",
  "Artes y Diseño": "#4f9fd9",
  "Ciencias Sociales y Humanidades": "#d68a3f",
  "Educación y Pedagogía": "#e65c79",
  "Ingeniería, Ciencia y Tecnología": "#3b82f6",
  "Negocios, Economía y Finanzas": "#b8942e",
  "Salud y Medicina": "#c65bea",
};

export default function FiltroBecas() {
  const { state } = useLocation();
  const answers = state?.answers || {};
  const { interestArea, educationLevel, scholarshipType, university } = answers;

  const normalize = (str = "") =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const typeSynonyms = {
    "Por situación económica": [
      "económico",
      "apoyo económico",
      "situación económica",
    ],
    "Por rendimiento académico": [
      "excelencia",
      "académica",
      "rendimiento académico",
    ],
    "Para estudiar en el extranjero": [
      "movilidad",
      "internacional",
      "extranjero",
    ],
    "Otra (por identidad, liderazgo, etc.)": [],
  };

  let resultados = scholarships;

  if (interestArea) {
    resultados = resultados.filter((b) =>
      normalize(b["Categoría"]).includes(normalize(interestArea)),
    );
  }

  if (Array.isArray(educationLevel) && educationLevel.length) {
    resultados = resultados.filter((b) =>
      educationLevel.some((lvl) =>
        normalize(b["Nivel Educativo"]).includes(normalize(lvl)),
      ),
    );
  }

  if (scholarshipType) {
    const syns = typeSynonyms[scholarshipType] || [];

    resultados = resultados.filter((b) => {
      if (!syns.length) return true;

      const tipo = normalize(b["Tipo de Beca"]);
      return syns.some((syn) => tipo.includes(normalize(syn)));
    });
  }

  if (university) {
    resultados = resultados.filter((b) =>
      normalize(b["Institución Otorgante"]).includes(normalize(university)),
    );
  }

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [currentPage, setCurrentPage] = useState(1);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(scholarships.map((b) => b["Categoría"])));
    return ["Todos", ...cats];
  }, []);

  const filtered = scholarships.filter((b) => {
    const q = search.toLowerCase();

    const matchesText =
      b["Categoría"].toLowerCase().includes(q) ||
      b["Nombre de la Beca"].toLowerCase().includes(q) ||
      b["Institución Otorgante"].toLowerCase().includes(q);

    const matchesCategory =
      selectedCategory === "Todos" || b["Categoría"] === selectedCategory;

    return matchesText && matchesCategory;
  });

  const perPage = 12;
  const totalPages = Math.ceil(filtered.length / perPage);
  const startIdx = (currentPage - 1) * perPage;
  const pageItems = filtered.slice(startIdx, startIdx + perPage);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  function getPaginationRange(currentPage, totalPages) {
    const delta = 1;
    const range = [];
    const left = currentPage - delta;
    const right = currentPage + delta;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages) {
        range.push(i);
      } else if (i >= left && i <= right) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }

    return range;
  }

  return (
    <main className="fb-page">
      <section className="fb-hero">
        <div className="fb-hero__inner">
          {/* <span className="fb-pill">Resultados ZY</span> */}

          <h1>Resultados para tu perfil</h1>

          <p>
            Estas son las becas que mejor hacen match con tus respuestas.
            También puedes seguir explorando más oportunidades disponibles.
          </p>

          {/* <div className="fb-hero__actions">
            <Link to="/becas" className="fb-back-link">
              ← Volver al quiz
            </Link>
          </div> */}
        </div>
      </section>

      <section className="fb-results">
        {/* <div className="fb-section-heading">
          <span>Recomendadas</span>
          <h2>Becas sugeridas para ti</h2>
          <p>
            Filtramos las oportunidades según tu área de interés, nivel
            educativo y tipo de apoyo que estás buscando.
          </p>
        </div> */}

        {resultados.length === 0 ? (
          <div className="fb-no-result">
            <h3>Aún no hay becas que hagan match contigo.</h3>
            <p>
              No te rindas. Puedes ajustar tus respuestas o explorar otras
              opciones disponibles abajo.
            </p>

            <Link to="/becas" className="fb-card-link">
              Volver al quiz
            </Link>
          </div>
        ) : (
          <ul className="fb-becas-list">
            {resultados.map((b) => {
              const originalIndex = scholarships.indexOf(b);
              const categoryColor =
                categoryColors[b["Categoría"]] || "var(--fb-purple)";

              return (
                <li
                  key={`${b["Nombre de la Beca"]}-${originalIndex}`}
                  className="fb-beca-card"
                >
                  <div className="fb-beca-card__image-wrap">
                    <img
                      src={b["Image URL"]}
                      alt={b["Nombre de la Beca"]}
                      className="fb-beca-card__img"
                    />
                  </div>

                  <div className="fb-beca-card__body">
                    <span
                      className="fb-beca-card__category"
                      style={{ "--category-color": categoryColor }}
                    >
                      {b["Categoría"]}
                    </span>

                    <h3 className="fb-beca-card__title">
                      {b["Nombre de la Beca"]}
                    </h3>

                    <div className="fb-beca-card__info">
                      <p>
                        <strong>Institución:</strong>{" "}
                        {b["Institución Otorgante"]}
                      </p>

                      <p>
                        <strong>Categoría:</strong> {b["Categoría"]}
                      </p>
                    </div>

                    <Link
                      to={`/becas/${originalIndex}`}
                      className="fb-card-link"
                    >
                      Ver detalles
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="fb-all-becas">
        <div className="fb-section-heading">
          <span>Explorar</span>
          <h2>Explora todas las becas</h2>
          <p>
            Busca por categoría, nombre de beca o institución otorgante para
            encontrar más oportunidades.
          </p>
        </div>

        <div className="fb-tools">
          <div className="fb-search-wrapper">
            <FiSearch className="fb-search-icon" />

            <input
              className="fb-search"
              type="text"
              placeholder="Filtrar por categoría, beca o institución…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="fb-category-filter">
            {categories.map((cat) => {
              const categoryColor =
                cat === "Todos"
                  ? "var(--fb-purple)"
                  : categoryColors[cat] || "var(--fb-purple)";

              const isActive = cat === selectedCategory;

              return (
                <button
                  key={cat}
                  type="button"
                  className={`fb-category-pill ${
                    isActive ? "fb-category-pill--active" : ""
                  }`}
                  style={{ "--category-color": categoryColor }}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCurrentPage(1);
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        <ul className="fb-becas-list">
          {pageItems.map((b) => {
            const originalIndex = scholarships.indexOf(b);
            const categoryColor =
              categoryColors[b["Categoría"]] || "var(--fb-purple)";

            return (
              <li
                key={`${b["Nombre de la Beca"]}-${originalIndex}`}
                className="fb-beca-card"
              >
                <div className="fb-beca-card__image-wrap">
                  <img
                    src={b["Image URL"]}
                    alt={b["Nombre de la Beca"]}
                    className="fb-beca-card__img"
                  />
                </div>

                <div className="fb-beca-card__body">
                  <span
                    className="fb-beca-card__category"
                    style={{ "--category-color": categoryColor }}
                  >
                    {b["Categoría"]}
                  </span>

                  <h3 className="fb-beca-card__title">
                    {b["Nombre de la Beca"]}
                  </h3>

                  <div className="fb-beca-card__info">
                    <p>
                      <strong>Institución:</strong> {b["Institución Otorgante"]}
                    </p>
                  </div>

                  <Link to={`/becas/${originalIndex}`} className="fb-card-link">
                    Ver detalles
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>

        {totalPages > 1 && (
          <div className="fb-pagination">
            <button
              type="button"
              className="fb-page-button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              aria-label="Página anterior"
            >
              ←
            </button>

            {getPaginationRange(currentPage, totalPages).map((item, idx) =>
              typeof item === "number" ? (
                <button
                  key={idx}
                  type="button"
                  className={`fb-page-button fb-page-button--number ${
                    item === currentPage ? "fb-page-button--active" : ""
                  }`}
                  onClick={() => setCurrentPage(item)}
                >
                  {item}
                </button>
              ) : (
                <span key={idx} className="fb-page-ellipsis">
                  {item}
                </span>
              ),
            )}

            <button
              type="button"
              className="fb-page-button"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              aria-label="Página siguiente"
            >
              →
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
